import {
  MoveSchema
} from "./chunk-ONOOJDCB.js";

// src/match.ts
import { z } from "zod";
var PROTOCOL_VERSION = 2;
var MIN_MATCH_PLAYERS = 2;
var MAX_MATCH_PLAYERS = 99;
var RoomKindSchema = z.enum(["queue", "custom"]);
var QUEUE_WAIT_MS = 3e4;
var MATCH_DURATION_MS = 12e4;
var RECONNECT_GRACE_MS = 1e4;
var OPPONENT_SCORE_BROADCAST_INTERVAL_MS = 200;
var MatchPhaseSchema = z.enum([
  "queued",
  // in matchmaker queue, no room assigned
  "lobby",
  // assigned to a RoomDO; waiting for QUEUE_WAIT_MS OR full
  "playing",
  // round in progress
  "ended"
  // round over; results being computed
]);
var MatchPlayerStateSchema = z.object({
  userId: z.number().int().positive(),
  displayName: z.string(),
  score: z.number().int().nonnegative(),
  /**
   * Final rank, assigned at match end. 1 = highest score.
   * null while the match is in lobby/playing phases.
   */
  finishedRank: z.number().int().positive().nullable()
});
var ClientJoinSchema = z.object({
  t: z.literal("join"),
  protocolVersion: z.literal(PROTOCOL_VERSION)
});
var ClientLeaveSchema = z.object({
  t: z.literal("leave")
});
var ClientMoveSchema = z.object({
  t: z.literal("move"),
  /** Move with rect + tsMs (ms since match start). Server replays via engine. */
  move: MoveSchema,
  /** Seed the client used to mount its board. Diagnostic-only for W2. */
  clientSeed: z.number().int().optional()
});
var ClientHeartbeatSchema = z.object({
  t: z.literal("hb")
});
var GRID_DEBUG_COLS = 17;
var GRID_DEBUG_ROWS = 10;
var ClientGridDebugSchema = z.object({
  t: z.literal("grid-debug"),
  /** Client-rendered grid: outer length = rows, inner length = cols, values 0..9. */
  gridValues: z.array(z.array(z.number().int().min(0).max(9)).length(GRID_DEBUG_COLS)).length(GRID_DEBUG_ROWS),
  /** Seed the client used to generate the grid (echoes ClientMoveSchema.clientSeed). */
  seed: z.number().int()
});
var ClientOwnerStartSchema = z.object({
  t: z.literal("owner-start")
});
var ClientOwnerLeaveAndTransferSchema = z.object({
  t: z.literal("owner-leave-and-transfer")
});
var ClientMessageSchema = z.discriminatedUnion("t", [
  ClientJoinSchema,
  ClientLeaveSchema,
  ClientMoveSchema,
  ClientHeartbeatSchema,
  ClientGridDebugSchema,
  ClientOwnerStartSchema,
  ClientOwnerLeaveAndTransferSchema
]);
var ServerWelcomeSchema = z.object({
  t: z.literal("welcome"),
  matchId: z.string().uuid(),
  yourUserId: z.number().int().positive(),
  /** Server timestamp at the moment of welcome — clients sync clock from this. */
  serverTimeMs: z.number().int().nonnegative(),
  phase: MatchPhaseSchema,
  /**
   * W2.2: room kind. Optional during the transition window so older
   * clients/tests parse without the new field. Defaults to 'queue' on the
   * client when absent.
   */
  roomKind: RoomKindSchema.optional(),
  /**
   * W2.2: owner userId for custom rooms; null for queue rooms. Optional during
   * transition; required clients ignore on null.
   */
  ownerUserId: z.number().int().positive().nullable().optional()
});
var ServerLobbyStateSchema = z.object({
  t: z.literal("lobby"),
  playerCount: z.number().int().min(0).max(MAX_MATCH_PLAYERS),
  /** ms remaining until forced start (or null if already at MAX_MATCH_PLAYERS). */
  remainingMs: z.number().int().nonnegative().nullable(),
  /**
   * W2.2: room kind echoed on every lobby frame so the client can keep its
   * UI branch (queue countdown vs custom Start button) authoritative even
   * across reconnects. Optional for back-compat; default 'queue' on absence.
   */
  roomKind: RoomKindSchema.optional(),
  /** W2.2: owner userId for custom rooms; null for queue. */
  ownerUserId: z.number().int().positive().nullable().optional()
});
var ServerStartSchema = z.object({
  t: z.literal("start"),
  /** Seed used to generate the shared starting board. All players see same board. */
  seed: z.number().int(),
  startedAtMs: z.number().int().nonnegative(),
  /** Roster snapshot at start. */
  players: z.array(MatchPlayerStateSchema)
});
var ServerTickSchema = z.object({
  t: z.literal("tick"),
  serverTimeMs: z.number().int().nonnegative(),
  remainingMs: z.number().int().nonnegative(),
  players: z.array(MatchPlayerStateSchema)
});
var ServerOpponentScoreSchema = z.object({
  t: z.literal("opponent-score"),
  userId: z.number().int().positive(),
  displayName: z.string(),
  score: z.number().int().nonnegative()
});
var ServerMatchEndSchema = z.object({
  t: z.literal("end"),
  finalRanking: z.array(MatchPlayerStateSchema),
  /** Server-validated winner (highest score). null if everyone DC'd. */
  winnerUserId: z.number().int().positive().nullable()
});
var ServerErrorSchema = z.object({
  t: z.literal("error"),
  reason: z.enum([
    "PROTOCOL_MISMATCH",
    "AUTH_FAILED",
    "MATCH_FULL",
    "INVALID_MOVE",
    "RATE_LIMITED",
    "KICKED",
    "INTERNAL"
  ]),
  details: z.string().optional()
});
var ServerOwnerChangedSchema = z.object({
  t: z.literal("owner-changed"),
  newOwnerUserId: z.number().int().positive(),
  prevOwnerUserId: z.number().int().positive()
});
var ServerRoomCanceledSchema = z.object({
  t: z.literal("room-canceled"),
  reason: z.enum(["owner-cancel", "owner-left-no-replacement", "ttl"])
});
var ServerMessageSchema = z.discriminatedUnion("t", [
  ServerWelcomeSchema,
  ServerLobbyStateSchema,
  ServerStartSchema,
  ServerTickSchema,
  ServerOpponentScoreSchema,
  ServerMatchEndSchema,
  ServerErrorSchema,
  ServerOwnerChangedSchema,
  ServerRoomCanceledSchema
]);
var QueueEnterRequestSchema = z.object({
  /** Optional region hint; falls back to CF colo. */
  regionHint: z.string().optional()
});
var QueueEnterResponseSchema = z.object({
  ok: z.literal(true),
  /** wss:// URL the client connects to. Includes one-time auth token. */
  wsUrl: z.string().url(),
  /** Match assigned (the same matchId arrives over WS welcome too). */
  matchId: z.string().uuid(),
  /** Estimated wait in seconds (0 if room already has 99 - 1 players). */
  estimatedWaitSec: z.number().int().nonnegative()
});
var QueueEnterErrorSchema = z.object({
  ok: z.literal(false),
  reason: z.enum(["NO_STAMINA", "ALREADY_QUEUED", "IN_MATCH", "RATE_LIMITED", "INTERNAL"])
});
var MatchHistoryEntrySchema = z.object({
  matchId: z.string().uuid(),
  score: z.number().int().nonnegative(),
  /** Server-recorded rank at match end. 1 = winner (highest score). */
  finishedRank: z.number().int().positive().nullable(),
  /** Total participants in that match (read off the attempt rows for that match_id). */
  totalPlayers: z.number().int().min(2).max(MAX_MATCH_PLAYERS),
  submittedAt: z.string().datetime()
});
var MatchHistoryResponseSchema = z.object({
  entries: z.array(MatchHistoryEntrySchema),
  /** Total count (across all pages). */
  total: z.number().int().nonnegative(),
  /** Echo back the limit/offset the request used. */
  limit: z.number().int().positive().max(50),
  offset: z.number().int().nonnegative()
});
var FRIEND_ROOM_CODE_REGEX = /^[0-9A-HJKMNP-TV-Z]{6}$/;
var FRIEND_ROOM_CODE_LENGTH = 6;
var RoomCreateRequestSchema = z.object({}).strict();
var RoomCreateResponseSchema = z.object({
  ok: z.literal(true),
  wsUrl: z.string().url(),
  matchId: z.string().uuid(),
  /** Crockford base32, 6 chars, uppercase, no I/L/O/U. */
  code: z.string().length(FRIEND_ROOM_CODE_LENGTH).regex(FRIEND_ROOM_CODE_REGEX),
  /** W2.2: always 'custom' for /room/create. Lets the web client branch UI. */
  roomKind: z.literal("custom").optional(),
  /** W2.2: owner is the caller. Optional during transition window. */
  ownerUserId: z.number().int().positive().optional(),
  /** W2.2: owner's chosen target size (default 2). Optional during transition. */
  targetSize: z.number().int().min(MIN_MATCH_PLAYERS).max(MAX_MATCH_PLAYERS).optional()
});
var RoomJoinRequestSchema = z.object({
  code: z.string().min(1).max(64)
});
var RoomJoinSuccessSchema = z.object({
  ok: z.literal(true),
  wsUrl: z.string().url(),
  matchId: z.string().uuid(),
  /** W2.2: always 'custom' for /room/join. */
  roomKind: z.literal("custom").optional(),
  /** W2.2: surface the room owner so the joiner can render "waiting for owner". */
  ownerUserId: z.number().int().positive().optional(),
  /** W2.2: target size echoed so both sides agree on auto-start threshold. */
  targetSize: z.number().int().min(MIN_MATCH_PLAYERS).max(MAX_MATCH_PLAYERS).optional()
});
var RoomErrorCodeSchema = z.enum([
  "LIVE_MATCH_DISABLED",
  "ROOM_NOT_FOUND",
  "INVALID_CODE",
  "NETWORK_ERROR",
  "STAMINA_EMPTY",
  "RATE_LIMITED",
  "INTERNAL"
]);
var RoomCreateErrorSchema = z.object({
  ok: z.literal(false),
  error: z.enum(["LIVE_MATCH_DISABLED", "STAMINA_EMPTY", "RATE_LIMITED", "INTERNAL"])
});
var RoomCreateResultSchema = z.discriminatedUnion("ok", [
  RoomCreateResponseSchema,
  RoomCreateErrorSchema
]);
var RoomJoinErrorSchema = z.object({
  ok: z.literal(false),
  error: z.enum([
    "LIVE_MATCH_DISABLED",
    "ROOM_NOT_FOUND",
    "INVALID_CODE",
    "NETWORK_ERROR",
    "STAMINA_EMPTY",
    "RATE_LIMITED",
    "INTERNAL"
  ])
});
var RoomJoinResultSchema = z.discriminatedUnion("ok", [
  RoomJoinSuccessSchema,
  RoomJoinErrorSchema
]);
var RoomCancelRequestSchema = z.object({
  matchId: z.string().min(1)
});
var RoomCancelSuccessSchema = z.object({
  ok: z.literal(true),
  /** True if this call performed the refund; false if idempotent second call. */
  refunded: z.boolean()
});
var RoomCancelErrorSchema = z.object({
  ok: z.literal(false),
  error: z.enum([
    "LIVE_MATCH_DISABLED",
    "ROOM_NOT_LOBBY",
    "NOT_OWNER",
    "RATE_LIMITED",
    "INTERNAL"
  ])
});
var RoomCancelResultSchema = z.discriminatedUnion("ok", [
  RoomCancelSuccessSchema,
  RoomCancelErrorSchema
]);

export {
  PROTOCOL_VERSION,
  MIN_MATCH_PLAYERS,
  MAX_MATCH_PLAYERS,
  RoomKindSchema,
  QUEUE_WAIT_MS,
  MATCH_DURATION_MS,
  RECONNECT_GRACE_MS,
  OPPONENT_SCORE_BROADCAST_INTERVAL_MS,
  MatchPhaseSchema,
  MatchPlayerStateSchema,
  ClientJoinSchema,
  ClientLeaveSchema,
  ClientMoveSchema,
  ClientHeartbeatSchema,
  GRID_DEBUG_COLS,
  GRID_DEBUG_ROWS,
  ClientGridDebugSchema,
  ClientOwnerStartSchema,
  ClientOwnerLeaveAndTransferSchema,
  ClientMessageSchema,
  ServerWelcomeSchema,
  ServerLobbyStateSchema,
  ServerStartSchema,
  ServerTickSchema,
  ServerOpponentScoreSchema,
  ServerMatchEndSchema,
  ServerErrorSchema,
  ServerOwnerChangedSchema,
  ServerRoomCanceledSchema,
  ServerMessageSchema,
  QueueEnterRequestSchema,
  QueueEnterResponseSchema,
  QueueEnterErrorSchema,
  MatchHistoryEntrySchema,
  MatchHistoryResponseSchema,
  FRIEND_ROOM_CODE_REGEX,
  FRIEND_ROOM_CODE_LENGTH,
  RoomCreateRequestSchema,
  RoomCreateResponseSchema,
  RoomJoinRequestSchema,
  RoomJoinSuccessSchema,
  RoomErrorCodeSchema,
  RoomCreateErrorSchema,
  RoomCreateResultSchema,
  RoomJoinErrorSchema,
  RoomJoinResultSchema,
  RoomCancelRequestSchema,
  RoomCancelSuccessSchema,
  RoomCancelErrorSchema,
  RoomCancelResultSchema
};
